import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { scrollToSection } from '../utils/scroll';
import { useI18n } from '../i18n/I18nContext';
import LanguageSwitcher from './LanguageSwitcher';

interface NavigationProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
}

function Navigation({ isMenuOpen, setIsMenuOpen }: NavigationProps) {
  const location = useLocation();
  const { t, localizedPath } = useI18n();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('');

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    const strippedPath = location.pathname.replace(/^\/(en|es|fr|it|ru|de|pt)(?=\/|$)/, '');
    if (strippedPath !== '/' && strippedPath !== '') {
      setActiveSection('');
      return;
    }
    const sectionIds = ['services', 'star-models', 'portfolio', 'about', 'partners', 'contact'];
    const updateActiveSection = () => {
      const passedSections = sectionIds
        .map((id) => document.getElementById(id))
        .filter((section): section is HTMLElement => Boolean(section))
        .filter((section) => section.getBoundingClientRect().top <= 180);
      const current = passedSections[passedSections.length - 1];
      setActiveSection(current?.id ?? '');
    };
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    updateActiveSection();
    return () => window.removeEventListener('scroll', updateActiveSection);
  }, [location.pathname]);

  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        scrollToSection(id);
      }, 0);
    }
  }, [location]);

  const navBg = isScrolled
    ? 'bg-white shadow-md border-b border-zinc-200'
    : 'bg-zinc-900/30 backdrop-blur-sm border-b border-zinc-800';

  const linkClass = isScrolled
    ? 'hover:text-[#FF5500] transition text-zinc-800'
    : 'hover:text-[#FDCB12] transition text-white';

  const mobileMenuBg = isScrolled
    ? 'bg-white/98 backdrop-blur-sm border-b border-zinc-200'
    : 'bg-zinc-900/95 backdrop-blur-sm';

  const mobileLinkClass = isScrolled
    ? 'text-lg hover:text-[#FF5500] transition text-left text-zinc-800'
    : 'text-lg hover:text-[#FDCB12] transition text-left text-white';
  const desktopClass = (section: string) => `${linkClass} nav-link ${location.pathname.replace(/^\/(en|es|fr|it|ru|de|pt)(?=\/|$)/, '') === '/' && activeSection === section ? 'is-active' : ''}`;

  const isHome = location.pathname.replace(/^\/(en|es|fr|it|ru|de|pt)(?=\/|$)/, '') === '/' || location.pathname.replace(/^\/(en|es|fr|it|ru|de|pt)(?=\/|$)/, '') === '';

  return (
    <nav className={`py-2 sm:py-4 sticky top-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="container mx-auto px-4 flex items-center justify-between">
        <Link to={localizedPath('/')}>
          <img
            src="/impresstars-logo.png"
            alt="Impresstars Logo"
            className={`h-10 sm:h-12 md:h-16 lg:h-20 w-auto select-none max-w-full animate-logo-float ${isScrolled ? "" : "brightness-0 invert"}`}
            loading="eager"
            aria-label="Impresstars"
          />
        </Link>
        <div className="hidden md:flex items-center space-x-8">
          {isHome ? (
            <button onClick={() => scrollToSection('services')} className={desktopClass('services')}>{t.nav.services}</button>
          ) : (
            <Link to={localizedPath('/#services')} className={desktopClass('services')}>{t.nav.services}</Link>
          )}
          <Link to={localizedPath('/#star-models')} className={desktopClass('star-models')}>{t.nav.starModel}</Link>
          <Link to={localizedPath('/#portfolio')} className={desktopClass('portfolio')}>{t.nav.portfolio}</Link>
          {isHome ? (
            <button onClick={() => scrollToSection('about')} className={desktopClass('about')}>{t.nav.about}</button>
          ) : (
            <Link to={localizedPath('/#about')} className={desktopClass('about')}>{t.nav.about}</Link>
          )}
          {isHome ? (
            <button onClick={() => scrollToSection('contact')} className={desktopClass('contact')}>{t.nav.contact}</button>
          ) : (
            <Link to={localizedPath('/#contact')} className={desktopClass('contact')}>{t.nav.contact}</Link>
          )}
          <LanguageSwitcher isScrolled={isScrolled} />
        </div>
        <div className="hidden md:flex items-center gap-4">
          {isHome ? (
            <button onClick={() => scrollToSection('contact')} className="button-shine bg-[#FDCB12] text-black px-6 py-2 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300 transform hover:-translate-y-0.5">
              {t.nav.getStarted}
            </button>
          ) : (
            <Link to={localizedPath('/#contact')} className="button-shine bg-[#FDCB12] text-black px-6 py-2 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300 transform hover:-translate-y-0.5">
              {t.nav.getStarted}
            </Link>
          )}
        </div>
        <button
          className={`menu-toggle md:hidden ${isMenuOpen ? 'is-open' : ''} ${isScrolled ? 'text-zinc-800' : 'text-white'}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
        >
          <span /><span /><span />
        </button>
      </div>
      <div className={`md:hidden fixed inset-x-0 top-[60px] sm:top-[73px] ${mobileMenuBg} transition-all duration-300 ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}>
        <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
          {isHome ? (
            <button onClick={() => { scrollToSection('services'); setIsMenuOpen(false); }} className={mobileLinkClass}>{t.nav.services}</button>
          ) : (
            <Link to={localizedPath('/#services')} onClick={() => setIsMenuOpen(false)} className={mobileLinkClass}>{t.nav.services}</Link>
          )}
          <Link to={localizedPath('/#star-models')} onClick={() => setIsMenuOpen(false)} className={mobileLinkClass}>{t.nav.starModel}</Link>
          <Link to={localizedPath('/#portfolio')} onClick={() => setIsMenuOpen(false)} className={mobileLinkClass}>{t.nav.portfolio}</Link>
          {isHome ? (
            <button onClick={() => { scrollToSection('about'); setIsMenuOpen(false); }} className={mobileLinkClass}>{t.nav.about}</button>
          ) : (
            <Link to={localizedPath('/#about')} onClick={() => setIsMenuOpen(false)} className={mobileLinkClass}>{t.nav.about}</Link>
          )}
          {isHome ? (
            <button onClick={() => { scrollToSection('contact'); setIsMenuOpen(false); }} className={mobileLinkClass}>{t.nav.contact}</button>
          ) : (
            <Link to={localizedPath('/#contact')} onClick={() => setIsMenuOpen(false)} className={mobileLinkClass}>{t.nav.contact}</Link>
          )}
          <LanguageSwitcher isScrolled={isScrolled} mobile />
          {isHome ? (
            <button onClick={() => { scrollToSection('contact'); setIsMenuOpen(false); }} className="bg-[#FDCB12] text-black px-6 py-2 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300 w-full">
              {t.nav.getStarted}
            </button>
          ) : (
            <Link to={localizedPath('/#contact')} onClick={() => setIsMenuOpen(false)} className="bg-[#FDCB12] text-black px-6 py-2 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300 w-full text-center">
              {t.nav.getStarted}
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navigation;
